/**
 * Cuando una revision NO puede cambiar nada.
 *
 * SPEC 19.7: en el cuerpo de `PUT /review`, un campo nulo significa "no lo
 * toques", nunca "borralo". De ahi se sigue algo que la pantalla tiene que
 * saber: **una revision que no aporta nada es una peticion que no puede cambiar
 * nada.** El servidor la acepta, responde 204 y deja la operacion como estaba,
 * asi que el boton parpadea y vuelve. Eso es lo que el propietario leyo como un
 * boton roto (BUGLOG, 2026-09-11).
 *
 * Vive aqui, fuera de React y sin DOM, para que se pueda probar con `node:test`
 * (SPEC 3.2): la regla es de dominio y no de pintado.
 */

export type ReviewDraft = {
  whatWentWell: string;
  whatToImprove: string;
  lesson: string;
  /** Los errores reconocidos y las confluencias, tal como estan marcados ahora. */
  mistakeIds: readonly string[];
  confluenceIds: readonly string[];
};

/** Lo que la operacion YA tiene guardado, para saber si la seleccion cambio. */
export type ReviewOriginal = {
  mistakeIds: readonly string[];
  confluenceIds: readonly string[];
};

/** Un campo con solo espacios es un campo vacio, y vacio se manda en nulo. */
export function blankText(text: string): string | null {
  const trimmed = text.trim();

  return trimmed === "" ? null : trimmed;
}

/**
 * La lista que hay que mandar, o NULO si la seleccion no cambio.
 *
 * Las dos cosas importan y son distintas:
 *
 *   - **Nulo** es "no lo toques". Se manda cuando la seleccion es la misma que ya
 *     esta guardada, para que una revision de texto no reescriba las etiquetas
 *     sin motivo.
 *   - **Una lista VACIA es una orden**: borra las que hubiera. Es la unica forma
 *     de quitar un error mal marcado, asi que no puede confundirse con nulo.
 *
 * El orden NO cuenta: marcar A y luego B es la misma seleccion que marcar B y
 * luego A, y mandarlo como cambio haria que guardar dos veces seguidas
 * reescribiera las filas por nada.
 */
export function idsToSend(
  selected: readonly string[],
  original: readonly string[],
): string[] | null {
  return sameIds(selected, original) ? null : [...selected];
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;

  const pool = new Set(right);

  return left.every((id) => pool.has(id));
}

/**
 * Cierto cuando la revision escrita no tiene nada que mandar. El boton de
 * guardar se desactiva con esto, y dice por que: enviarla seria pedirle al
 * servidor que no haga nada.
 *
 * Los tres textos en blanco YA NO BASTAN para desactivarlo: desde el ADR 0011 la
 * revision tambien marca errores y confluencias, y cambiar solo una etiqueta es
 * un cambio de verdad. Si esto siguiera mirando solo los textos, marcar un error
 * y pulsar guardar no haria nada y el boton volveria a parecer roto -- el mismo
 * fallo de 2026-09-11 por otra puerta.
 */
export function reviewChangesNothing(draft: ReviewDraft, original: ReviewOriginal): boolean {
  return (
    blankText(draft.whatWentWell) === null &&
    blankText(draft.whatToImprove) === null &&
    blankText(draft.lesson) === null &&
    idsToSend(draft.mistakeIds, original.mistakeIds) === null &&
    idsToSend(draft.confluenceIds, original.confluenceIds) === null
  );
}
